import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, UserCheck, DollarSign, TrendingUp, Link2, Copy, ExternalLink, 
  Sparkles, Filter, ArrowUpDown, Calendar, Gift, CreditCard, ChevronDown,
  ChevronUp, Clock, Search
} from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

// ==================== COMPONENTES AUXILIARES ====================

// Skeleton Loader
const SkeletonCard = () => (
  <div className="animate-pulse space-y-6">
    <div className="bg-[#1A1A1A] rounded-2xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="h-8 w-48 bg-[#2A2A2A] rounded"></div>
        <div className="h-8 w-24 bg-[#2A2A2A] rounded-full"></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-[#0D0D0D] rounded-xl p-5">
            <div className="h-6 w-6 bg-[#2A2A2A] rounded mb-3"></div>
            <div className="h-8 w-20 bg-[#2A2A2A] rounded mb-2"></div>
            <div className="h-4 w-16 bg-[#2A2A2A] rounded"></div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-[#1A1A1A] rounded-2xl p-6 h-64"></div>
  </div>
);

// Mini Line Chart (SVG puro - sem dependências)
const MiniLineChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
        Sem dados de ganhos ainda
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);
  const range = maxValue - minValue || 1;
  
  const width = 100;
  const padding = 5;
  const chartHeight = height - 40;
  
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1 || 1)) * (width - padding * 2);
    const y = padding + ((maxValue - d.value) / range) * (chartHeight - padding * 2);
    return { x, y, ...d };
  });
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  // Área preenchida
  const areaD = `${pathD} L ${points[points.length - 1]?.x || 0} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(percent => {
          const y = padding + (percent / 100) * (chartHeight - padding * 2);
          return (
            <line 
              key={percent}
              x1={padding} 
              y1={y} 
              x2={width - padding} 
              y2={y} 
              stroke="#2A2A2A" 
              strokeWidth="0.5"
              strokeDasharray="2,2"
            />
          );
        })}
        
        {/* Área preenchida */}
        <path 
          d={areaD} 
          fill="url(#goldGradient)" 
          opacity="0.3"
        />
        
        {/* Linha principal */}
        <path 
          d={pathD} 
          fill="none" 
          stroke="#C79F5E" 
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Pontos */}
        {points.map((p, i) => (
          <circle 
            key={i} 
            cx={p.x} 
            cy={p.y} 
            r="3" 
            fill="#C79F5E"
            stroke="#0D0D0D"
            strokeWidth="1.5"
          />
        ))}
        
        {/* Gradiente */}
        <defs>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#C79F5E" />
            <stop offset="100%" stopColor="#C79F5E" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Labels dos meses */}
      <div className="flex justify-between px-2 mt-1">
        {data.map((d, i) => (
          <span key={i} className="text-[10px] text-gray-500">{d.label}</span>
        ))}
      </div>
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

const GodparentDashboard = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [invites, setInvites] = useState([]);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Filtros
  const [affiliateFilter, setAffiliateFilter] = useState('all'); // all, verified, pending
  const [affiliateSort, setAffiliateSort] = useState('commission'); // commission, subscribers, recent
  const [searchTerm, setSearchTerm] = useState('');
  
  // Expansão de seções
  const [showHistory, setShowHistory] = useState(false);
  const [showChart, setShowChart] = useState(true);
  
  useEffect(() => {
    if (userId) {
      fetchDashboard();
      fetchInvites();
    }
  }, [userId]);
  
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/godparent/dashboard?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('not_godparent');
      } else {
        console.error('Erro ao carregar dashboard:', err);
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/godparent/my-invites?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvites(response.data.invites || []);
    } catch (err) {
      console.error('Erro ao carregar convites:', err);
    }
  };
  
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/godparent/earnings-history?user_id=${userId}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/godparent/create-invite?user_id=${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigator.clipboard.writeText(response.data.invite_url);
      toast.success('Link criado e copiado!');
      fetchInvites();
    } catch (err) {
      toast.error('Erro ao criar convite');
      console.error(err);
    } finally {
      setCreatingInvite(false);
    }
  };
  
  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Filtrar e ordenar afiliados
  const filteredAffiliates = useMemo(() => {
    if (!dashboard?.affiliates) return [];
    
    let filtered = [...dashboard.affiliates];
    
    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filtro por status
    if (affiliateFilter === 'verified') {
      filtered = filtered.filter(a => a.is_verified);
    } else if (affiliateFilter === 'pending') {
      filtered = filtered.filter(a => !a.is_verified);
    }
    
    // Ordenação
    switch (affiliateSort) {
      case 'commission':
        filtered.sort((a, b) => (b.your_commission || 0) - (a.your_commission || 0));
        break;
      case 'subscribers':
        filtered.sort((a, b) => (b.subscribers || 0) - (a.subscribers || 0));
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
    }
    
    return filtered;
  }, [dashboard?.affiliates, affiliateFilter, affiliateSort, searchTerm]);
  
  // Dados para o gráfico (últimos 6 meses)
  const chartData = useMemo(() => {
    if (!history || history.length === 0) {
      // Dados de exemplo se não houver histórico
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
      return months.map((label, i) => ({ label, value: 0 }));
    }
    
    // Agrupar por mês
    const monthlyData = {};
    const now = new Date();
    
    // Inicializar últimos 6 meses
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      monthlyData[key] = { label, value: 0 };
    }
    
    // Somar valores por mês
    history.forEach(h => {
      if (h.created_at) {
        const date = new Date(h.created_at);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (monthlyData[key]) {
          monthlyData[key].value += h.amount || 0;
        }
      }
    });
    
    return Object.values(monthlyData);
  }, [history]);
  
  // Carregar histórico quando expandir
  useEffect(() => {
    if (showHistory && history.length === 0) {
      fetchHistory();
    }
  }, [showHistory]);
  
  // Carregar histórico para o gráfico
  useEffect(() => {
    if (userId && showChart) {
      fetchHistory();
    }
  }, [userId, showChart]);
  
  // Não é padrinho/madrinha
  if (error === 'not_godparent' || (!loading && !dashboard)) {
    return null;
  }
  
  if (loading) {
    return <SkeletonCard />;
  }
  
  if (error) {
    return null;
  }
  
  const { godparent_info, stats, affiliates, commission_rate, profile_link, referral_link } = dashboard;
  
  // KPI Cards
  const kpiCards = [
    {
      icon: Users,
      value: stats.total_affiliates,
      label: 'Total Afiliados',
      color: 'text-blue-400',
      bgGradient: 'from-blue-600/20 to-blue-900/10',
      borderColor: 'border-blue-500/30'
    },
    {
      icon: UserCheck,
      value: stats.verified_affiliates,
      label: 'Verificados',
      color: 'text-green-400',
      bgGradient: 'from-green-600/20 to-green-900/10',
      borderColor: 'border-green-500/30'
    },
    {
      icon: DollarSign,
      value: formatCurrency(stats.total_earnings),
      label: 'Ganhos Totais',
      color: 'text-[#C79F5E]',
      bgGradient: 'from-[#C79F5E]/20 to-[#8B6914]/10',
      borderColor: 'border-[#C79F5E]/30',
      isHighlight: true
    },
    {
      icon: TrendingUp,
      value: formatCurrency(stats.this_month_earnings),
      label: 'Este Mês',
      color: 'text-pink-400',
      bgGradient: 'from-pink-600/20 to-pink-900/10',
      borderColor: 'border-pink-500/30'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Card Principal */}
      <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#C79F5E]/20 overflow-hidden shadow-2xl">
        {/* Header */}
        <CardHeader className="pb-4 border-b border-[#C79F5E]/10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-black" />
              </div>
              <div>
                <CardTitle className="text-xl sm:text-2xl text-white">
                  {godparent_info.title}
                </CardTitle>
                <p className="text-sm text-gray-400">
                  Membro desde {godparent_info.since ? formatDate(godparent_info.since) : 'hoje'}
                </p>
              </div>
            </div>
            <Badge className="bg-gradient-to-r from-[#C79F5E] to-[#8B6914] text-black font-bold px-4 py-2 text-sm shadow-lg">
              {commission_rate}% de comissão
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {kpiCards.map((kpi, index) => (
              <div 
                key={index}
                className={`relative bg-gradient-to-br ${kpi.bgGradient} rounded-xl p-4 sm:p-5 border ${kpi.borderColor} transition-all hover:scale-[1.02] hover:shadow-lg`}
              >
                <div className="flex items-start justify-between mb-2">
                  <kpi.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${kpi.color} opacity-80`} />
                  {kpi.isHighlight && (
                    <span className="text-[10px] bg-[#C79F5E]/20 text-[#C79F5E] px-2 py-0.5 rounded-full">
                      Total
                    </span>
                  )}
                </div>
                <p className={`text-xl sm:text-2xl lg:text-3xl font-bold ${kpi.color} mb-1`}>
                  {kpi.value}
                </p>
                <p className="text-xs sm:text-sm text-gray-400">{kpi.label}</p>
              </div>
            ))}
          </div>
          
          {/* Gráfico de Evolução */}
          <div className="bg-[#0D0D0D] rounded-xl p-4 sm:p-5 border border-[#C79F5E]/10">
            <button 
              onClick={() => setShowChart(!showChart)}
              className="w-full flex items-center justify-between mb-4"
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#C79F5E]" />
                <h4 className="text-white font-semibold">Evolução dos Ganhos</h4>
              </div>
              {showChart ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            
            {showChart && (
              <div className="h-40">
                <MiniLineChart data={chartData} height={140} />
              </div>
            )}
          </div>
          
          {/* Seção: Recrutar Criadores */}
          <div className="bg-gradient-to-r from-[#4D1A4A]/30 to-[#0D0D0D] rounded-xl p-4 sm:p-5 border border-[#4D1A4A]/30">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4 flex-col sm:flex-row">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#4D1A4A]/50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#C79F5E]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-base sm:text-lg">Recrutar Criadores</h4>
                  <p className="text-xs sm:text-sm text-gray-400">Crie links para convidar novos criadores</p>
                </div>
              </div>
              <Button
                onClick={createInvite}
                disabled={creatingInvite}
                className="w-full sm:w-auto bg-gradient-to-r from-[#C79F5E] to-[#8B6914] hover:from-[#D4AF6E] hover:to-[#9B7924] text-black font-semibold min-h-[48px] px-6"
              >
                {creatingInvite ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    Criando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Criar Link
                  </span>
                )}
              </Button>
            </div>
            
            {/* Lista de Convites */}
            {invites.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {invites.slice(0, 5).map((invite) => (
                  <div
                    key={invite.id}
                    className={`flex items-center justify-between rounded-lg p-3 text-sm ${
                      invite.used 
                        ? 'bg-green-900/20 border border-green-500/20' 
                        : 'bg-[#1A1A1A] border border-[#C79F5E]/10'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <code className="text-[#C79F5E] text-xs font-mono">{invite.token}</code>
                      {invite.used ? (
                        <span className="text-green-400 text-xs flex items-center gap-1 mt-1">
                          <UserCheck className="w-3 h-3" />
                          {invite.used_by_info?.name || 'Criador'}
                        </span>
                      ) : (
                        <span className="text-yellow-400 text-xs flex items-center gap-1 mt-1">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                          Aguardando
                        </span>
                      )}
                    </div>
                    {!invite.used && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(`${BASE_URL}/aplicar?godparent_ref=${invite.token}`)}
                        className="border-[#C79F5E]/30 text-[#C79F5E] text-xs ml-3 min-h-[36px]"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Links de Perfil e Referência */}
          <div className="grid sm:grid-cols-2 gap-4">
            {profile_link && (
              <div className="bg-[#0D0D0D] rounded-xl p-4 border border-[#C79F5E]/20">
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-4 h-4 text-[#C79F5E]" />
                  <p className="text-sm text-gray-400">Seu perfil</p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#1A1A1A] text-[#C79F5E] px-3 py-2 rounded-lg text-xs truncate">
                    {BASE_URL}{profile_link}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => copyLink(`${BASE_URL}${profile_link}`)}
                    className="bg-[#C79F5E] hover:bg-[#D4AF6E] text-black min-h-[40px]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {referral_link && (
              <div className="bg-[#0D0D0D] rounded-xl p-4 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-purple-400" />
                  <p className="text-sm text-gray-400">Link para recrutar</p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#1A1A1A] text-purple-400 px-3 py-2 rounded-lg text-xs truncate">
                    {BASE_URL}{referral_link}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => copyLink(`${BASE_URL}${referral_link}`)}
                    className="bg-purple-600 hover:bg-purple-500 text-white min-h-[40px]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de Afiliados com Filtros */}
      {affiliates.length > 0 && (
        <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#C79F5E]/20">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C79F5E]" />
                Seus Afiliados ({filteredAffiliates.length})
              </CardTitle>
              
              {/* Filtros */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Busca */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 w-32 sm:w-40 focus:outline-none focus:border-[#C79F5E]/50"
                  />
                </div>
                
                {/* Filtro Status */}
                <select
                  value={affiliateFilter}
                  onChange={(e) => setAffiliateFilter(e.target.value)}
                  className="bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C79F5E]/50"
                >
                  <option value="all">Todos</option>
                  <option value="verified">Verificados</option>
                  <option value="pending">Pendentes</option>
                </select>
                
                {/* Ordenação */}
                <select
                  value={affiliateSort}
                  onChange={(e) => setAffiliateSort(e.target.value)}
                  className="bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C79F5E]/50"
                >
                  <option value="commission">Maior Comissão</option>
                  <option value="subscribers">Mais Assinantes</option>
                  <option value="recent">Mais Recentes</option>
                </select>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {filteredAffiliates.map((affiliate) => (
                <a
                  key={affiliate.id}
                  href={`/creator/${affiliate.id}`}
                  className="flex items-center gap-3 sm:gap-4 bg-[#0D0D0D] hover:bg-[#1A1A1A] rounded-xl p-3 sm:p-4 border border-[#C79F5E]/10 hover:border-[#C79F5E]/30 transition-all group"
                >
                  <div className="relative">
                    <img
                      src={affiliate.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(affiliate.name)}&background=C79F5E&color=000`}
                      alt={affiliate.name}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#C79F5E]/30 group-hover:border-[#C79F5E] transition-colors"
                    />
                    {affiliate.is_verified && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-[#0D0D0D]">
                        <UserCheck className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm sm:text-base font-medium truncate group-hover:text-[#C79F5E] transition-colors">
                        {affiliate.name}
                      </p>
                      {affiliate.is_verified && (
                        <Badge variant="outline" className="border-green-500/30 text-green-400 text-[10px] px-1.5 py-0 hidden sm:inline-flex">
                          Verificado
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {affiliate.subscribers}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {formatCurrency(affiliate.total_revenue)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#C79F5E] font-bold text-sm sm:text-base">
                      {formatCurrency(affiliate.your_commission)}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500">sua comissão</p>
                  </div>
                </a>
              ))}
              
              {filteredAffiliates.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  Nenhum afiliado encontrado com os filtros selecionados
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Histórico de Transações */}
      <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#C79F5E]/20">
        <CardHeader className="pb-4">
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between"
          >
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C79F5E]" />
              Histórico de Transações
            </CardTitle>
            {showHistory ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
        </CardHeader>
        
        {showHistory && (
          <CardContent>
            {loadingHistory ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse flex items-center gap-4 bg-[#0D0D0D] rounded-xl p-4">
                    <div className="w-10 h-10 bg-[#2A2A2A] rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-[#2A2A2A] rounded mb-2"></div>
                      <div className="h-3 w-24 bg-[#2A2A2A] rounded"></div>
                    </div>
                    <div className="h-5 w-20 bg-[#2A2A2A] rounded"></div>
                  </div>
                ))}
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {history.map((tx, index) => (
                  <div
                    key={tx.id || index}
                    className="flex items-center gap-3 sm:gap-4 bg-[#0D0D0D] rounded-xl p-3 sm:p-4 border border-[#C79F5E]/10"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.source_type === 'subscription' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-pink-500/20 text-pink-400'
                    }`}>
                      {tx.source_type === 'subscription' ? (
                        <CreditCard className="w-5 h-5" />
                      ) : (
                        <Gift className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {tx.affiliate_name || 'Afiliado'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{tx.source_type === 'subscription' ? 'Assinatura' : 'Presente'}</span>
                        <span>•</span>
                        <span>{formatDateTime(tx.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[#C79F5E] font-bold text-sm">
                        +{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        de {formatCurrency(tx.original_amount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Nenhuma transação ainda</p>
                <p className="text-sm text-gray-500 mt-1">
                  Quando seus afiliados receberem pagamentos, as comissões aparecerão aqui
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      
      {/* Estado Vazio Geral */}
      {affiliates.length === 0 && invites.length === 0 && (
        <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#C79F5E]/20">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#C79F5E]/20 to-[#8B6914]/10 flex items-center justify-center">
                <Users className="w-10 h-10 text-[#C79F5E]" />
              </div>
              <h4 className="text-xl text-white font-semibold mb-2">Comece a Recrutar!</h4>
              <p className="text-gray-400 text-sm max-w-md mx-auto mb-6">
                Crie links de convite para recrutar criadores e ganhe {commission_rate}% de comissão permanente sobre toda a receita deles!
              </p>
              <Button
                onClick={createInvite}
                disabled={creatingInvite}
                className="bg-gradient-to-r from-[#C79F5E] to-[#8B6914] hover:from-[#D4AF6E] hover:to-[#9B7924] text-black font-semibold min-h-[48px] px-8"
              >
                <Link2 className="w-5 h-5 mr-2" />
                Criar Primeiro Link
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GodparentDashboard;
